import React, { useEffect, useState } from "react";
import { Cloud, LogOut, Loader2, Download, Users, TrendingUp, Mail, FileText, StickyNote, Star, FileDown, Paperclip, CalendarDays, Video, Plug, Unplug, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import NotesDialog from "@/components/admin/NotesDialog";
import {
  adminLogin,
  fetchMe,
  fetchLeads,
  deleteLead,
  rateLead,
  fetchShortApplications,
  deleteShortApplication,
  rateShortApplication,
  fetchMeetingBookings,
  deleteMeetingBooking,
  fetchGcalStatus,
  startGcalOAuth,
  disconnectGcal,
  fetchNoteCounts,
  downloadShortApplicationPolicy,
  getToken,
  setToken,
  clearToken,
} from "@/lib/api";

const money = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
};

const fmtMeetingDate = (isoDate) => {
  try {
    // isoDate is a plain YYYY-MM-DD; parse as local date, not UTC, to avoid TZ shifts.
    const [y, m, d] = isoDate.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  } catch {
    return isoDate;
  }
};

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function LoginView({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await adminLogin(email.trim(), password);
      setToken(data.access_token);
      toast.success(`Welcome back, ${data.user?.name || "Admin"}`);
      onSuccess(data.user);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-sand px-6" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <span className="grid place-items-center w-11 h-11 rounded-full bg-forest text-cream">
            <Cloud className="h-5 w-5" />
          </span>
          <span className="font-serif text-3xl text-forest">Advanced Life Policies Admin</span>
        </div>
        <form
          onSubmit={submit}
          className="rounded-3xl bg-white border border-forest/10 shadow-xl p-8"
          data-testid="admin-login-form"
        >
          <h1 className="font-serif text-2xl text-forest">Sign in</h1>
          <p className="text-sm text-forest/60 mt-1 mb-6">View and manage your leads.</p>

          <div className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-forest">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                className="mt-2 h-12 rounded-xl"
                data-testid="admin-email"
                required
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-forest">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-12 rounded-xl"
                data-testid="admin-password"
                required
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full h-12 rounded-full bg-forest hover:bg-forest-700 text-cream font-semibold transition-all duration-300 hover:-translate-y-0.5 active:scale-95"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </div>
        </form>
        <p className="text-center text-xs text-forest/40 mt-6">Advanced Life Policies · Private area</p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, value, label }) {
  return (
    <div className="rounded-2xl bg-white border border-forest/10 p-6 flex items-center gap-4" data-testid="admin-stat">
      <span className="grid place-items-center w-12 h-12 rounded-xl bg-sage/15 text-forest">
        <Icon className="h-6 w-6" />
      </span>
      <div>
        <p className="font-serif text-3xl text-forest leading-none">{value}</p>
        <p className="text-xs uppercase tracking-[0.14em] text-forest/50 font-semibold mt-1">{label}</p>
      </div>
    </div>
  );
}

function RatingPicker({ value, onChange, testid }) {
  const options = [
    { key: "hot", label: "Good", dotClass: "bg-emerald-500", ringClass: "ring-emerald-500/40" },
    { key: "warm", label: "Warm", dotClass: "bg-amber-500", ringClass: "ring-amber-500/40" },
    { key: "cold", label: "Bad", dotClass: "bg-red-500", ringClass: "ring-red-500/40" },
  ];
  return (
    <div className="inline-flex items-center gap-1" data-testid={testid}>
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onChange(o.key)}
            title={active ? `${o.label} (click to clear)` : o.label}
            data-testid={`${testid}-${o.key}`}
            className={`grid place-items-center h-6 w-6 rounded-full hover:bg-forest/5 transition-colors ${active ? `ring-2 ${o.ringClass}` : ""}`}
          >
            <span className={`inline-block h-3 w-3 rounded-full ${o.dotClass} ${active ? "" : "opacity-30 hover:opacity-70 transition-opacity"}`} />
          </button>
        );
      })}
    </div>
  );
}

function RowDeleteBtn({ onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title="Delete"
      className="ml-1 grid place-items-center h-8 w-8 rounded-full text-forest/40 hover:text-terracotta hover:bg-terracotta/10 transition-colors"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  );
}

function NoteCell({ count = 0, onOpen, testid }) {
  return (
    <div className="flex items-center justify-end gap-2">
      {count > 0 && (
        <span
          data-testid="note-star"
          title={`${count} note${count > 1 ? "s" : ""}`}
          className="inline-flex items-center gap-0.5 text-terracotta"
        >
          <Star className="h-4 w-4 fill-terracotta" />
          <span className="text-xs font-bold">{count}</span>
        </span>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-forest/50 hover:text-terracotta opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
      >
        <StickyNote className="h-4 w-4" /> Note
      </button>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [apps, setApps] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [gcal, setGcal] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("leads");
  const [notesTarget, setNotesTarget] = useState(null);
  const [noteCounts, setNoteCounts] = useState({ lead: {}, application: {} });

  useEffect(() => {
    const onErr = (err) => {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      }
    };
    Promise.allSettled([
      fetchLeads(),
      fetchShortApplications(),
      fetchMeetingBookings(),
      fetchNoteCounts("lead"),
      fetchNoteCounts("application"),
      fetchGcalStatus(),
    ])
      .then(([l, a, m, lc, ac, g]) => {
        if (l.status === "fulfilled") setLeads(l.value);
        else onErr(l.reason);
        if (a.status === "fulfilled") setApps(a.value);
        else onErr(a.reason);
        if (m.status === "fulfilled") setMeetings(m.value);
        else onErr(m.reason);
        setNoteCounts({
          lead: lc.status === "fulfilled" ? lc.value : {},
          application: ac.status === "fulfilled" ? ac.value : {},
        });
        if (g.status === "fulfilled") setGcal(g.value);
      })
      .finally(() => setLoading(false));

    // Post-OAuth redirect toasts
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("gcal");
    if (flag === "connected") {
      toast.success("Google Calendar connected!");
      fetchGcalStatus().then(setGcal).catch(() => {});
      window.history.replaceState(null, "", "/admin");
    } else if (flag) {
      toast.error(`Google Calendar connect failed (${flag}). Please try again.`);
      window.history.replaceState(null, "", "/admin");
    }
  }, [onLogout]);

  const handleCountChange = (target, count) =>
    setNoteCounts((prev) => ({
      ...prev,
      [target.type]: { ...prev[target.type], [target.id]: count },
    }));

  const downloadPolicy = async (a) => {
    try {
      await downloadShortApplicationPolicy(a.id, a.policy_file_name);
    } catch {
      toast.error("Couldn't download the policy file.");
    }
  };

  const connectGoogle = async () => {
    try {
      const { authorization_url } = await startGcalOAuth();
      window.location.href = authorization_url;
    } catch {
      toast.error("Couldn't start Google connect.");
    }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google Calendar? New bookings won't auto-generate Meet links.")) return;
    try {
      await disconnectGcal();
      setGcal({ connected: false });
      toast.success("Google Calendar disconnected.");
    } catch {
      toast.error("Couldn't disconnect.");
    }
  };

  const handleDelete = async (kind, id, label) => {
    if (!window.confirm(`Delete ${kind} "${label}"? This can't be undone.`)) return;
    try {
      if (kind === "lead") {
        await deleteLead(id);
        setLeads((ls) => ls.filter((x) => x.id !== id));
      } else if (kind === "application") {
        await deleteShortApplication(id);
        setApps((as) => as.filter((x) => x.id !== id));
      } else if (kind === "meeting") {
        await deleteMeetingBooking(id);
        setMeetings((ms) => ms.filter((x) => x.id !== id));
      }
      toast.success("Deleted.");
    } catch {
      toast.error("Couldn't delete — please try again.");
    }
  };

  const setRating = async (kind, id, current, next) => {
    const value = current === next ? null : next; // toggle off if clicking same colour
    try {
      if (kind === "lead") {
        await rateLead(id, value);
        setLeads((ls) => ls.map((x) => (x.id === id ? { ...x, rating: value } : x)));
      } else if (kind === "application") {
        await rateShortApplication(id, value);
        setApps((as) => as.map((x) => (x.id === id ? { ...x, rating: value } : x)));
      }
    } catch {
      toast.error("Couldn't update rating.");
    }
  };

  const withQuote = leads.filter((l) => l.quote).length;
  const totalPotential = leads.reduce((s, l) => s + (l.quote?.annual_savings || 0), 0);

  const exportCsv = () => {
    const rows = [
      ["Name", "Email", "Phone", "Source", "Best time", "Age", "Coverage", "Monthly premium", "Annual savings", "Date"],
      ...leads.map((l) => [
        l.name,
        l.email || "",
        l.phone || "",
        l.source || "estimator",
        l.preferred_time || "",
        l.age || "",
        l.coverage || "",
        l.quote?.monthly_premium || "",
        l.quote?.annual_savings || "",
        l.created_at,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `advanced-life-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <header className="bg-forest text-cream">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-cream/15">
              <Cloud className="h-4 w-4" />
            </span>
            <span className="font-serif text-2xl">Advanced Life Policies Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-cream/70">{user?.email}</span>
            <Button
              onClick={onLogout}
              variant="outline"
              data-testid="admin-logout"
              className="rounded-full border-cream/25 bg-transparent text-cream hover:bg-cream/10 h-10"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 lg:px-10 py-10">
        {/* Tabs */}
        <div className="inline-flex rounded-full bg-white border border-forest/15 p-1 mb-8" data-testid="admin-tabs">
          {[
            ["leads", "Leads", leads.length],
            ["applications", "Applications", apps.length],
            ["meetings", "Meetings", meetings.length],
          ].map(([key, label, count]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                tab === key ? "bg-forest text-cream" : "text-forest/60 hover:text-forest"
              }`}
            >
              {label} <span className={tab === key ? "text-cream/70" : "text-forest/40"}>({count})</span>
            </button>
          ))}
        </div>

        {tab === "leads" ? (
          <>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h1 className="font-serif text-4xl text-forest">Leads</h1>
                <p className="text-forest/60 mt-1">Everyone who signed up to save with Advanced Life Policies.</p>
              </div>
              <Button
                onClick={exportCsv}
                disabled={!leads.length}
                data-testid="admin-export"
                className="rounded-full bg-terracotta hover:bg-terracotta-dark text-cream h-11 px-6 font-semibold transition-all duration-300 hover:-translate-y-0.5"
              >
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>

            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              <StatCard icon={Users} value={leads.length} label="Total leads" />
              <StatCard icon={TrendingUp} value={withQuote} label="With a quote" />
              <StatCard icon={Mail} value={money(Math.round(totalPotential))} label="Potential yearly savings" />
            </div>

            <div className="mt-8 rounded-2xl bg-white border border-forest/10 overflow-hidden">
              {loading ? (
                <div className="p-16 grid place-items-center text-forest/50">
                  <Loader2 className="h-7 w-7 animate-spin" />
                </div>
              ) : leads.length === 0 ? (
                <div className="p-16 text-center text-forest/50" data-testid="admin-empty">
                  <Cloud className="h-8 w-8 mx-auto mb-3 text-sage" />
                  No leads yet. They&apos;ll appear here the moment someone signs up.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-sand/60">
                        <TableHead>Name</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Rating</TableHead>
                        <TableHead>Age</TableHead>
                        <TableHead>Coverage</TableHead>
                        <TableHead>Quote</TableHead>
                        <TableHead>Saves / yr</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.map((l) => (
                        <TableRow key={l.id} data-testid="admin-lead-row" className="group">
                          <TableCell className="font-semibold text-forest">
                            {l.name}
                            {l.source === "callback" && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-terracotta/15 text-terracotta text-[10px] font-bold uppercase tracking-wide px-2 py-0.5" data-testid="lead-callback-badge">
                                Callback
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-forest/70">
                            {l.email && <div>{l.email}</div>}
                            {l.phone && <div className="text-xs text-forest/50">{l.phone}</div>}
                            {l.preferred_time && <div className="text-xs text-forest/50">Best time: {l.preferred_time}</div>}
                            {!l.email && !l.phone && <span className="text-forest/40">—</span>}
                          </TableCell>
                          <TableCell>
                            <RatingPicker
                              value={l.rating}
                              onChange={(next) => setRating("lead", l.id, l.rating, next)}
                              testid={`lead-rating-${l.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-forest/70">{l.age || "—"}</TableCell>
                          <TableCell className="text-forest/70">{money(l.coverage || l.quote?.coverage)}</TableCell>
                          <TableCell className="text-forest/70">
                            {l.quote ? `${money(l.quote.monthly_premium)}/mo` : "—"}
                          </TableCell>
                          <TableCell className="font-semibold text-forest">
                            {l.quote ? money(l.quote.annual_savings) : "—"}
                          </TableCell>
                          <TableCell className="text-forest/60 whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <NoteCell
                                count={noteCounts.lead[l.id] || 0}
                                testid="lead-notes-btn"
                                onOpen={() => setNotesTarget({ type: "lead", id: l.id, label: `${l.name} · ${l.email || l.phone || ""}` })}
                              />
                              <RowDeleteBtn onClick={() => handleDelete("lead", l.id, l.name)} testid="lead-delete-btn" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        ) : tab === "applications" ? (
          <>
            <div>
              <h1 className="font-serif text-4xl text-forest">Applications</h1>
              <p className="text-forest/60 mt-1">Callback applications submitted through the apply flow. Download attached policies below.</p>
            </div>

            <div className="mt-8 rounded-2xl bg-white border border-forest/10 overflow-hidden">
              {loading ? (
                <div className="p-16 grid place-items-center text-forest/50">
                  <Loader2 className="h-7 w-7 animate-spin" />
                </div>
              ) : apps.length === 0 ? (
                <div className="p-16 text-center text-forest/50" data-testid="admin-apps-empty">
                  <FileText className="h-8 w-8 mx-auto mb-3 text-sage" />
                  No applications yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-sand/60">
                        <TableHead>Reference</TableHead>
                        <TableHead>Applicant</TableHead>
                        <TableHead>Rating</TableHead>
                        <TableHead>Advisor</TableHead>
                        <TableHead>Has insurance</TableHead>
                        <TableHead>Policy</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {apps.map((a) => (
                        <TableRow key={a.id} data-testid="admin-app-row" className="group">
                          <TableCell className="font-mono text-xs font-semibold text-forest">{a.reference}</TableCell>
                          <TableCell className="text-forest/80">
                            <div className="font-semibold">{a.name}</div>
                            <div className="text-xs text-forest/50">{a.email}</div>
                            <div className="text-xs text-forest/50">{a.phone}</div>
                            {a.dob && <div className="text-xs text-forest/50">DOB: {a.dob}</div>}
                            {a.address && <div className="text-xs text-forest/50 max-w-[220px] truncate" title={a.address}>{a.address}</div>}
                          </TableCell>
                          <TableCell>
                            <RatingPicker
                              value={a.rating}
                              onChange={(next) => setRating("application", a.id, a.rating, next)}
                              testid={`app-rating-${a.id}`}
                            />
                          </TableCell>
                          <TableCell><YesNoPill on={a.has_advisor} /></TableCell>
                          <TableCell>
                            <YesNoPill on={a.has_insurance} />
                            {a.has_insurance ? (
                              <div className="mt-1 text-xs text-forest/55 space-y-0.5" data-testid="app-cover-details">
                                {a.premium_amount && <div>Paying: {a.premium_amount}/mo</div>}
                                {a.insurance_company && <div>{a.insurance_company}</div>}
                                {a.policy_type && <div>{a.policy_type}</div>}
                              </div>
                            ) : (
                              a.looking_for && <div className="mt-1 text-xs text-forest/55" data-testid="app-cover-details">Wants: {a.looking_for}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            {a.policy_file_path ? (
                              <button
                                onClick={() => downloadPolicy(a)}
                                data-testid="app-policy-download"
                                className="inline-flex items-center gap-1.5 rounded-full bg-forest/10 hover:bg-forest hover:text-cream text-forest text-xs font-semibold px-3 py-1.5 transition-colors max-w-[220px]"
                                title={a.policy_file_name}
                              >
                                <Paperclip className="h-3.5 w-3.5 shrink-0" />
                                <span className="truncate">{a.policy_file_name || "Download"}</span>
                              </button>
                            ) : a.has_policy ? (
                              <span className="text-xs text-forest/45">Yes, not attached</span>
                            ) : (
                              <span className="text-xs text-forest/45">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-forest/60 whitespace-nowrap">{fmtDate(a.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <NoteCell
                                count={noteCounts.application[a.id] || 0}
                                testid="app-notes-btn"
                                onOpen={() => setNotesTarget({ type: "application", id: a.id, label: `${a.reference} · ${a.name}` })}
                              />
                              <RowDeleteBtn onClick={() => handleDelete("application", a.id, a.name)} testid="app-delete-btn" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <h1 className="font-serif text-4xl text-forest">Meetings</h1>
                <p className="text-forest/60 mt-1">Booked calls scheduled through the Book-a-meeting form.</p>
              </div>
              {gcal.connected ? (
                <div className="flex items-center gap-3 rounded-full bg-sage/15 border border-sage/40 px-4 py-2" data-testid="gcal-connected">
                  <span className="grid place-items-center h-7 w-7 rounded-full bg-sage/40 text-forest">
                    <Video className="h-3.5 w-3.5" />
                  </span>
                  <div className="text-sm">
                    <div className="font-semibold text-forest leading-tight">Google Calendar connected</div>
                    <div className="text-xs text-forest/60 leading-tight">{gcal.email}</div>
                  </div>
                  <button onClick={disconnectGoogle} data-testid="gcal-disconnect" className="ml-2 text-forest/50 hover:text-terracotta transition-colors" title="Disconnect">
                    <Unplug className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <Button
                  onClick={connectGoogle}
                  data-testid="gcal-connect"
                  className="rounded-full bg-forest hover:bg-forest-700 text-cream h-11 px-5 font-semibold transition-all duration-300 hover:-translate-y-0.5"
                >
                  <Plug className="h-4 w-4 mr-2" /> Connect Google Calendar
                </Button>
              )}
            </div>

            <div className="mt-8 rounded-2xl bg-white border border-forest/10 overflow-hidden">
              {loading ? (
                <div className="p-16 grid place-items-center text-forest/50">
                  <Loader2 className="h-7 w-7 animate-spin" />
                </div>
              ) : meetings.length === 0 ? (
                <div className="p-16 text-center text-forest/50" data-testid="admin-meetings-empty">
                  <CalendarDays className="h-8 w-8 mx-auto mb-3 text-sage" />
                  No meetings booked yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-sand/60">
                        <TableHead>Attendee</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Requested slot</TableHead>
                        <TableHead>Google Meet</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead>Booked</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {meetings.map((m) => (
                        <TableRow key={m.id} data-testid="admin-meeting-row">
                          <TableCell className="font-semibold text-forest">{m.name}</TableCell>
                          <TableCell className="text-forest/70">
                            <div className="text-sm">{m.email}</div>
                            {m.phone && <div className="text-xs text-forest/50">{m.phone}</div>}
                          </TableCell>
                          <TableCell className="text-forest">
                            <div className="font-semibold">{fmtMeetingDate(m.meeting_date)}</div>
                            <div className="text-xs text-forest/60">{m.meeting_time}</div>
                          </TableCell>
                          <TableCell>
                            {m.meet_link ? (
                              <a
                                href={m.meet_link}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="meet-link"
                                className="inline-flex items-center gap-1.5 rounded-full bg-forest/10 hover:bg-forest hover:text-cream text-forest text-xs font-semibold px-3 py-1.5 transition-colors"
                              >
                                <Video className="h-3.5 w-3.5" /> Join
                              </a>
                            ) : (
                              <span className="text-xs text-forest/40">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-forest/70 max-w-xs">
                            <span className="line-clamp-2 text-sm" title={m.notes || ""}>
                              {m.notes || <span className="text-forest/40">—</span>}
                            </span>
                          </TableCell>
                          <TableCell className="text-forest/60 whitespace-nowrap">
                            <div className="flex items-center justify-between gap-2">
                              <span>{fmtDate(m.created_at)}</span>
                              <RowDeleteBtn onClick={() => handleDelete("meeting", m.id, m.name)} testid="meeting-delete-btn" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      <NotesDialog target={notesTarget} onClose={() => setNotesTarget(null)} onCountChange={handleCountChange} />
    </div>
  );
}

function YesNoPill({ on }) {
  return (
    <span className={`inline-block rounded-full text-xs font-semibold px-2.5 py-0.5 ${on ? "bg-sage/25 text-forest" : "bg-forest/5 text-forest/50"}`}>
      {on ? "Yes" : "No"}
    </span>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    fetchMe()
      .then(setUser)
      .catch(() => clearToken())
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    clearToken();
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen grid place-items-center bg-sand">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  return user ? <Dashboard user={user} onLogout={logout} /> : <LoginView onSuccess={setUser} />;
}
